/**
 * What a menu is made of.
 *
 * Types only, no logic — so the spec can be imported by a surface that builds a menu without
 * pulling in the component, and by `menuNav.ts` without a cycle.
 *
 * Every member here already exists in one of the three menus this replaces, which is the test for
 * whether a kind belongs: `caption` and `note` look like decoration and are not
 * (`SentenceMenu`'s `Move to log` heading and its `No other tabs open` line, `Library`'s
 * `Move to folder` heading and its `No folders yet`), and `sub` is the dim mono folder name that
 * makes two logs called `04` in different works tellable apart.
 */

export type MenuItemSpec =
  | {
      kind: "item";
      id: string;
      label: string;
      /** Right-aligned mono glyph — the shortcut this item duplicates, e.g. `A`, `click`. */
      hint?: string;
      /** A dim mono second line. The folder a destination log lives in, and nothing else so far. */
      sub?: string;
      /** A leading swatch or glyph. The five card flags are what this is for. */
      swatch?: string;
      danger?: boolean;
      disabled?: boolean;
      /**
       * A toggle's state, drawn as a leading `✓` column — the confusion-group submenu (日本-601),
       * where choosing a ticked group removes the card from it. `undefined` draws no column at all,
       * so an ordinary item keeps its left edge.
       */
      checked?: boolean;
      onSelect: () => void;
    }
  | {
      kind: "submenu";
      id: string;
      label: string;
      items: MenuItemSpec[];
      /** Shown in place of an empty list — `No other tabs open`, `No folders yet`. */
      empty?: string;
    }
  | {
      /**
       * One row holding a HORIZONTAL strip of colour targets — Review's flag picker (`7d`).
       *
       * A row rather than five `item`s with `swatch` set, because both frames draw the five flags
       * side by side: 26px squares in the desktop menu, 44px flex targets across the phone sheet.
       * Stacked as ordinary items they would be five near-identical rows whose only content is a
       * colour, which is the shape the design explicitly does not use — a flag means whatever you
       * decide it means, so there is nothing to label and nothing to read down.
       *
       * `clear` is the sixth target and is deliberately not a sixth swatch: "no flag" is the
       * absence of a colour, so it draws as an outlined `✕`.
       */
      kind: "swatches";
      id: string;
      swatches: MenuSwatch[];
      clear?: Omit<MenuSwatch, "color">;
    }
  | { kind: "caption"; id: string; label: string }
  | { kind: "separator"; id: string }
  | { kind: "note"; id: string; label: string };

export interface MenuSwatch {
  id: string;
  /** Never rendered as text — it is the accessible name, because a colour has none. */
  label: string;
  color: string;
  selected: boolean;
  onSelect: () => void;
}

/**
 * Can this row be focused by the arrow keys? Captions, separators and notes cannot.
 *
 * **Nor can a `swatches` row, and that is not an oversight.** `moveFocus` walks rows vertically; a
 * strip of six targets is navigated across, so treating it as one focusable row would put `↓` on a
 * row that `↓` cannot then move within. Every flag has its own direct route anyway — the flag chip
 * in the action bar, and Cards' own flag menu — so what is lost is a keyboard path to a control
 * whose entire content is "pick a colour".
 */
export function isSelectable(item: MenuItemSpec): boolean {
  if (item.kind === "item") return item.disabled !== true;
  return item.kind === "submenu";
}
